import { MailCheck, MailWarning } from "lucide-react";

import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { Spinner } from "@src/shared/components/ui/spinner";

export interface EmailVerificationSettingsCardProps {
  email: string;
  isVerified: boolean;
  isSendingVerificationEmail?: boolean;
  verificationEmailError?: string | null;
  verificationEmailSent?: boolean;
  onSendVerificationEmail?: () => void;
}

export function EmailVerificationSettingsCard({
  email,
  isVerified,
  isSendingVerificationEmail = false,
  verificationEmailError = null,
  verificationEmailSent = false,
  onSendVerificationEmail,
}: EmailVerificationSettingsCardProps) {
  if (isVerified) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        aria-labelledby="email-verification-heading"
        as="section"
        className="mb-8 rounded-[12px] border border-border bg-background px-5 py-4"
        gap="md"
      >
        <MailCheck aria-hidden="true" className="size-4 text-primary" />
        <VerticalFlexbox gap="xs">
          <Text
            as="h3"
            fontWeight={600}
            id="email-verification-heading"
            sizing="sm"
          >
            Email verified
          </Text>
          <Text appearance="subdued" sizing="sm">
            {email}
          </Text>
        </VerticalFlexbox>
      </HorizontalFlexbox>
    );
  }

  return (
    <HorizontalFlexbox
      alignItems="center"
      aria-labelledby="email-verification-heading"
      as="section"
      className="mb-8 flex-col rounded-[12px] border border-border bg-background px-5 py-4 sm-stitch:flex-row"
      gap="md"
      justifyContent="space-between"
    >
      <HorizontalFlexbox alignItems="flex-start" gap="md">
        <MailWarning
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-amber-600"
        />
        <VerticalFlexbox gap="xs">
          <Text
            as="h3"
            fontWeight={600}
            id="email-verification-heading"
            sizing="sm"
          >
            Verify your email
          </Text>
          <Text appearance="subdued" sizing="sm">
            {email}
          </Text>
          <Text appearance="subdued" sizing="sm">
            Verify this email address to keep your account secure.
          </Text>
          {verificationEmailSent ? (
            <Text className="text-primary" role="status" sizing="sm">
              Verification email sent. Check your inbox and follow the link.
            </Text>
          ) : null}
          {verificationEmailError ? (
            <Text appearance="danger" role="alert" sizing="sm">
              {verificationEmailError}
            </Text>
          ) : null}
        </VerticalFlexbox>
      </HorizontalFlexbox>
      <Button
        className="shrink-0"
        disabled={isSendingVerificationEmail}
        onClick={onSendVerificationEmail}
        type="button"
      >
        {isSendingVerificationEmail ? (
          <Spinner className="mr-1" size="sm" />
        ) : (
          <MailCheck aria-hidden="true" className="size-4" />
        )}
        {isSendingVerificationEmail ? "Sending..." : "Send verification email"}
      </Button>
    </HorizontalFlexbox>
  );
}
